/* eslint-disable camelcase */
/* eslint-disable react/jsx-props-no-spreading */
import {
  Box,
  Checkbox,
  FormControlLabel,
  PaperProps,
  SxProps,
  TableCellProps,
  TableContainerProps,
  Theme,
} from '@mui/material';
import {
  MaterialReactTable,
  MaterialReactTableProps,
  MRT_Column,
  MRT_PaginationState,
  MRT_Row,
  MRT_RowData,
  MRT_TableInstance,
  MRT_TableState,
  MRT_Updater,
} from 'material-react-table';
import * as React from 'react';
import { tableColors } from '~/theme';
import TableCell from '~/views/Dashboard/Analytics/TableCell';

type OmittedProps<TData extends MRT_RowData> = Omit<
  Partial<MRT_TableState<TData>>,
  'isLoading' | 'showAlertBanner' | 'showProgressBars' | 'pagination'
>;

export interface CustomState<TData extends MRT_RowData> extends OmittedProps<TData> {
  isLoading?: boolean;
  isFetching?: boolean;
  isError?: boolean;
  pagination?: MRT_PaginationState;
  showAlertBanner?: boolean;
}

export type BaseDataTableProps<TData extends MRT_RowData> = Prettify<
  Omit<MaterialReactTableProps<TData>, 'state' | 'table'>
>;

export const muiTopToolbarPropsSx: SxProps<Theme> = {
  '& .MuiBox-root': {
    display: 'flex',
    alignItems: 'center',
    padding: 0,
    margin: '2px 0',
  },
};

const TABLE_VIEWPORT_MIN_HEIGHT = 200;
// DashboardContainer bottom padding plus a little breathing room below pagination.
const TABLE_PAGE_BOTTOM_PADDING = 20;
const TABLE_BOTTOM_TOOLBAR_FALLBACK_HEIGHT = 52;

function getBottomToolbarHeight(tableContainer: HTMLDivElement): number {
  const paper = tableContainer.closest('.MuiPaper-root');
  const bottomToolbar = paper?.lastElementChild;

  if (bottomToolbar && bottomToolbar !== tableContainer) {
    return bottomToolbar.getBoundingClientRect().height;
  }

  return TABLE_BOTTOM_TOOLBAR_FALLBACK_HEIGHT;
}

function getViewportHeight(): number {
  return window.visualViewport?.height ?? window.innerHeight;
}

function measureTableContainerMaxHeight(tableContainer: HTMLDivElement): number {
  const { top: containerTop } = tableContainer.getBoundingClientRect();
  const bottomToolbarHeight = getBottomToolbarHeight(tableContainer);

  return Math.max(
    getViewportHeight() - containerTop - bottomToolbarHeight - TABLE_PAGE_BOTTOM_PADDING,
    TABLE_VIEWPORT_MIN_HEIGHT,
  );
}

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref && typeof ref === 'object' && 'current' in ref) {
    // Ref objects from consumers are intentionally updated here.
    // eslint-disable-next-line no-param-reassign
    (ref as React.MutableRefObject<T | null>).current = value;
  }
}

function isPlainSxObject(part: SxProps<Theme>): boolean {
  return typeof part === 'object' && part !== null && !Array.isArray(part);
}

/**
 * Resolve an sx part to a plain object for shallow merging.
 * MRT always does `...(parseFromValuesOrFunc(sx, theme))` into an object literal, so the
 * value after parseFromValuesOrFunc must be a plain object, never an sx array.
 */
function resolveSxPart(part: SxProps<Theme>, theme: Theme): Record<string, unknown> {
  if (!part || typeof part === 'boolean') {
    return {};
  }
  if (typeof part === 'function') {
    return resolveSxPart(part(theme) as SxProps<Theme>, theme);
  }
  if (Array.isArray(part)) {
    return Object.assign({}, ...part.map((entry) => resolveSxPart(entry as SxProps<Theme>, theme)));
  }
  return part as Record<string, unknown>;
}

// MRT spreads resolved sx into a plain object; arrays break that path, so always merge to
// one object (or one theme callback that returns an object).
function composeSx(
  ...parts: Array<SxProps<Theme> | undefined | null | false>
): SxProps<Theme> | undefined {
  const sxParts = parts.filter((part): part is SxProps<Theme> => Boolean(part));
  if (sxParts.length === 0) {
    return undefined;
  }
  if (sxParts.length === 1) {
    return sxParts[0];
  }
  if (sxParts.every(isPlainSxObject)) {
    return Object.assign({}, ...sxParts);
  }

  return (theme: Theme) => Object.assign({}, ...sxParts.map((part) => resolveSxPart(part, theme)));
}

function mergeTableElementProps<T extends { sx?: SxProps<Theme> }>(
  defaultProps: T,
  consumerProps?: T,
): T {
  if (!consumerProps) {
    return defaultProps;
  }

  return {
    ...defaultProps,
    ...consumerProps,
    sx: composeSx(defaultProps.sx, consumerProps.sx),
  };
}

function consumerDefinesContainerHeight<TData extends MRT_RowData>(
  consumerContainerProps?: MaterialReactTableProps<TData>['muiTableContainerProps'],
): boolean {
  if (!consumerContainerProps) {
    return false;
  }
  if (typeof consumerContainerProps === 'function') {
    return true;
  }
  return consumerContainerProps.sx != null;
}

function useTableContainerMaxHeight(fullHeightTable: boolean, remeasureKey = '') {
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const tableContainerRef = React.useRef<HTMLDivElement | null>(null);
  const maxHeightRef = React.useRef<number | null>(null);
  const [maxHeightPx, setMaxHeightPx] = React.useState<number | undefined>(undefined);

  // Measured height is set as inline !important (beats MRT's clamp) and mirrored in sx via
  // maxHeightPx. Inline wins in DevTools; clearMaxHeight clears both. Consumer container sx skips this.
  const applyMaxHeight = React.useCallback((container: HTMLDivElement, nextMaxHeight: number) => {
    tableContainerRef.current = container;
    maxHeightRef.current = nextMaxHeight;
    setMaxHeightPx(nextMaxHeight);
    container.style.setProperty('max-height', `${nextMaxHeight}px`, 'important');
    container.style.setProperty('overflow', 'auto', 'important');
  }, []);

  const clearMaxHeight = React.useCallback(() => {
    maxHeightRef.current = null;
    setMaxHeightPx(undefined);

    if (tableContainerRef.current) {
      tableContainerRef.current.style.removeProperty('max-height');
      tableContainerRef.current.style.removeProperty('overflow');
      tableContainerRef.current = null;
    }
  }, []);

  const measureAndApply = React.useCallback(() => {
    if (fullHeightTable) {
      return;
    }

    const container = anchorRef.current?.querySelector<HTMLDivElement>('.MuiTableContainer-root');
    if (!container) {
      return;
    }

    applyMaxHeight(container, measureTableContainerMaxHeight(container));
  }, [applyMaxHeight, fullHeightTable]);

  const setTableContainerRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      tableContainerRef.current = node;

      if (node && !fullHeightTable && maxHeightRef.current != null) {
        applyMaxHeight(node, maxHeightRef.current);
      }
    },
    [applyMaxHeight, fullHeightTable],
  );

  React.useLayoutEffect(() => {
    if (fullHeightTable) {
      clearMaxHeight();
      return undefined;
    }

    const scheduleUpdate = () => {
      measureAndApply();
      window.requestAnimationFrame(measureAndApply);
    };

    scheduleUpdate();

    window.addEventListener('resize', scheduleUpdate);
    const { visualViewport } = window;
    visualViewport?.addEventListener('resize', scheduleUpdate);
    visualViewport?.addEventListener('scroll', scheduleUpdate);

    const wrapper = anchorRef.current;
    const mutationObserver =
      wrapper && typeof MutationObserver !== 'undefined'
        ? new MutationObserver(scheduleUpdate)
        : null;
    if (wrapper && mutationObserver) {
      mutationObserver.observe(wrapper, { childList: true, subtree: true });
    }

    let paperResizeObserver: ResizeObserver | null = null;
    const connectPaperObserver = () => {
      const container = wrapper?.querySelector<HTMLDivElement>('.MuiTableContainer-root');
      const paper = container?.closest('.MuiPaper-root');
      if (!paper || typeof ResizeObserver === 'undefined') {
        return;
      }

      paperResizeObserver?.disconnect();
      paperResizeObserver = new ResizeObserver(scheduleUpdate);
      paperResizeObserver.observe(paper);
    };

    connectPaperObserver();

    return () => {
      window.removeEventListener('resize', scheduleUpdate);
      visualViewport?.removeEventListener('resize', scheduleUpdate);
      visualViewport?.removeEventListener('scroll', scheduleUpdate);
      mutationObserver?.disconnect();
      paperResizeObserver?.disconnect();
    };
  }, [clearMaxHeight, fullHeightTable, measureAndApply, remeasureKey]);

  return { anchorRef, setTableContainerRef, maxHeightPx };
}

type MuiTableBodyRowPropsArgs<TData extends MRT_RowData> = {
  isDetailPanel?: boolean;
  row: MRT_Row<TData>;
  staticRowIndex: number;
  table: MRT_TableInstance<TData>;
};

function mergeMuiTableBodyRowProps<TData extends MRT_RowData>(
  consumerRowProps: MaterialReactTableProps<TData>['muiTableBodyRowProps'],
) {
  return (rowProps: MuiTableBodyRowPropsArgs<TData>) => {
    // use staticRowIndex (current visual position) so striping re-applies when rows reorder e.g. after sort/status change (MRT memo bust)
    const { staticRowIndex, isDetailPanel } = rowProps;

    const defaultProps = {
      sx: {
        ...(!isDetailPanel && staticRowIndex % 2 !== 0
          ? { backgroundColor: tableColors.rowAlt }
          : {}),
      },
    };

    const consumerProps =
      typeof consumerRowProps === 'function' ? consumerRowProps(rowProps) : consumerRowProps ?? {};

    return {
      ...defaultProps,
      ...consumerProps,
      sx: composeSx(defaultProps.sx, consumerProps.sx),
    };
  };
}

function readStoredPageSize(pageSizeKey: string) {
  try {
    return parseInt(localStorage.getItem(pageSizeKey) ?? '100', 10);
  } catch {
    return 100;
  }
}

export default function DataTable<TData extends MRT_RowData>({
  data = [],
  columns,
  state,
  initialState,
  Actions = undefined,
  currency,
  formatNumbers = true,
  isDianomi = false,
  autoResetPageIndex = false,
  enableColumnPinning = true,
  tableId,
  horizontalBorderOnly = true,
  muiTableBodyRowProps: consumerRowProps,
  muiTablePaperProps: consumerPaperProps,
  muiTableContainerProps: consumerContainerProps,
  muiTableHeadCellProps: consumerHeadCellProps,
  ...rest
}: Prettify<
  BaseDataTableProps<TData> & {
    state?: CustomState<TData>;
    Actions?: React.ReactNode;
    currency?: string;
    formatNumbers?: boolean;
    isDianomi?: boolean;
    tableId?: string;
    horizontalBorderOnly?: boolean;
  }
>) {
  const pageSizeKey = tableId ? `tablePageSize:${tableId}` : 'tablePageSize';

  const [fullHeightTable, setFullHeightTable] = React.useState(false);
  const [pagination, setPagination] = React.useState(() => ({
    pageIndex: initialState?.pagination?.pageIndex ?? 0,
    pageSize: readStoredPageSize(pageSizeKey),
  }));

  const handleFullHeightToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFullHeight = event.target.checked;
    const basePageSize = readStoredPageSize(pageSizeKey) || 100;
    setFullHeightTable(!fullHeightTable);
    setPagination((prev) => ({
      ...prev,
      pageSize: newFullHeight && isDianomi ? data.length + 1 : basePageSize,
    }));
  };

  const onPaginationChange = React.useCallback(
    (updater: MRT_Updater<MRT_PaginationState>) => {
      setPagination((prevState) => {
        if (typeof updater === 'function') {
          const newState = updater(prevState);
          if (newState.pageSize !== prevState.pageSize) {
            try {
              localStorage.setItem(pageSizeKey, newState.pageSize.toString());
            } catch {
              // localStorage is missing in some in-app browsers, such as Apple Mail.
            }
          }
          return newState;
        }
        return updater;
      });
    },
    [pageSizeKey],
  );

  const useAutoMaxHeight = !consumerDefinesContainerHeight(consumerContainerProps);

  const { anchorRef, setTableContainerRef, maxHeightPx } = useTableContainerMaxHeight(
    fullHeightTable || !useAutoMaxHeight,
    `${data.length}:${pagination.pageIndex}:${pagination.pageSize}`,
  );

  const defaultPaperProps = React.useMemo(
    (): PaperProps => ({
      elevation: 0,
      sx: {
        border: 'none',
        backgroundColor: 'transparent',
      },
    }),
    [],
  );

  const defaultContainerProps = React.useMemo((): TableContainerProps => {
    const scrollProps = (() => {
      if (!useAutoMaxHeight) {
        return {};
      }
      if (fullHeightTable) {
        return { maxHeight: 'auto' };
      }
      if (maxHeightPx) {
        return { maxHeight: `${maxHeightPx}px`, overflow: 'auto' as const };
      }
      return {};
    })();

    return {
      sx: {
        borderRadius: '6px',
        border: `1px solid ${tableColors.borderLight}`,
        ...scrollProps,
      },
    };
  }, [fullHeightTable, maxHeightPx, useAutoMaxHeight]);

  const mergedTablePaperProps = React.useCallback(
    (props: { table: MRT_TableInstance<TData> }) => {
      const consumerProps =
        typeof consumerPaperProps === 'function' ? consumerPaperProps(props) : consumerPaperProps;

      return mergeTableElementProps(defaultPaperProps, consumerProps);
    },
    [consumerPaperProps, defaultPaperProps],
  );

  const mergedTableContainerProps = React.useCallback(
    (props: { table: MRT_TableInstance<TData> }) => {
      const consumerProps =
        typeof consumerContainerProps === 'function'
          ? consumerContainerProps(props)
          : consumerContainerProps;

      const merged = mergeTableElementProps(defaultContainerProps, consumerProps);

      return {
        ...merged,
        ref: (node: HTMLDivElement | null) => {
          setTableContainerRef(node);
          assignRef(consumerProps?.ref, node);
        },
      };
    },
    [consumerContainerProps, defaultContainerProps, setTableContainerRef],
  );

  const mergedTableHeadCellProps = React.useCallback(
    (props: { column: MRT_Column<TData>; table: MRT_TableInstance<TData> }): TableCellProps => {
      const isGroupHeader = (props.column.columns?.length ?? 0) > 0;
      const rootGroupColumns = props.table
        .getAllColumns()
        .filter((column) => !column.parent && (column.columns?.length ?? 0) > 0);
      const isFirstGroupHeader = rootGroupColumns[0]?.id === props.column.id;
      const defaultProps: TableCellProps = {
        sx: {
          fontWeight: 600,
          fontSize: '0.8rem',
          letterSpacing: '0.04em',
          color: 'text.secondary',
          py: 0.75,
          ...(isGroupHeader && !isFirstGroupHeader
            ? { borderLeft: `2px solid ${tableColors.borderLight}` }
            : {}),
        },
      };
      const consumerProps =
        typeof consumerHeadCellProps === 'function'
          ? consumerHeadCellProps(props)
          : consumerHeadCellProps;

      return mergeTableElementProps(defaultProps, consumerProps);
    },
    [consumerHeadCellProps],
  );

  return (
    <Box ref={anchorRef} sx={{ width: '100%' }}>
      <MaterialReactTable
        columns={columns || []}
        data={data || []} // data is undefined on first render
        autoResetPageIndex={autoResetPageIndex}
        enableStickyHeader={rest.enableStickyHeader ?? true}
        enableTableFooter
        enableDensityToggle={false}
        enableColumnFilterModes
        enableColumnPinning={enableColumnPinning}
        enableMultiSort
        enableFullScreenToggle={false}
        muiTableContainerProps={mergedTableContainerProps}
        muiTablePaperProps={mergedTablePaperProps}
        muiBottomToolbarProps={{
          sx: {
            '& .MuiToolbar-gutters': {
              boxShadow: 'none',
            },
            '& .MuiTablePagination-root': {
              display: fullHeightTable ? 'none' : 'flex',
            },
          },
        }}
        muiTableBodyRowProps={mergeMuiTableBodyRowProps(consumerRowProps)}
        muiTableBodyCellProps={{
          sx: {
            fontSize: '0.8125rem',
            py: 0.5,
            height: '56px',
            borderLeft: horizontalBorderOnly ? 'none' : `2px solid ${tableColors.borderLight}`,
          },
        }}
        muiTableHeadCellProps={mergedTableHeadCellProps}
        muiTopToolbarProps={{
          sx: muiTopToolbarPropsSx,
        }}
        muiToolbarAlertBannerProps={
          state?.isError
            ? {
                color: 'error',
                children:
                  "We couldn't load this data. Check your connection and try refreshing the page.",
              }
            : undefined
        }
        renderTopToolbarCustomActions={() => Actions}
        renderBottomToolbarCustomActions={() => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isDianomi && (
              <Box
                sx={{
                  opacity: 0,
                  '&:hover, &:focus-within': {
                    opacity: 1,
                  },
                  transition: 'opacity 0.2s ease-out',
                  padding: '4px 8px',
                  height: '100%',
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={fullHeightTable}
                      onChange={handleFullHeightToggle}
                      color="primary"
                    />
                  }
                  label="Full table view"
                />
              </Box>
            )}
          </Box>
        )}
        onPaginationChange={onPaginationChange}
        renderEmptyRowsFallback={({ table }) => {
          const emptyStyle = {
            p: 2,
            textAlign: 'center',
            color: 'text.secondary',
            fontSize: '0.8125rem',
          } as const;

          if (data.length === 0 && table.getState().showAlertBanner) {
            return (
              <Box sx={emptyStyle}>
                Something went wrong loading this data. Try refreshing the page.
              </Box>
            );
          }

          if (data.length === 0) {
            return (
              <Box sx={emptyStyle}>
                No data to display yet. Data will appear here once available.
              </Box>
            );
          }
          if (table.getFilteredRowModel().rows.length === 0) {
            return (
              <Box sx={emptyStyle}>
                No results match your current filters. Try adjusting or clearing your filter
                criteria.
              </Box>
            );
          }
          return (
            <Box sx={emptyStyle}>No data to display yet. Data will appear here once available.</Box>
          );
        }}
        defaultColumn={{
          Cell: ({ renderedCellValue, column, row }) => {
            let columnId = column.id;
            if (columnId.toLowerCase().includes('custom')) {
              [columnId] = columnId.split('.');
            }

            return (
              <TableCell
                value={row.original[columnId] ?? renderedCellValue}
                column={{
                  header: column.columnDef.header,
                  accessor: column.columnDef.accessorKey as string,
                }}
                row={row.original}
                currency={currency ?? ''}
                formatNumbers={formatNumbers}
              />
            );
          },
        }}
        state={{
          density: 'compact' as const,
          pagination,
          ...state,
          isLoading: state?.isLoading || false,
          showAlertBanner: !!(state?.isError || state?.showAlertBanner),
          showProgressBars: state?.isFetching || false,
        }}
        initialState={{
          sorting: data[0] && 'date' in data[0] ? [{ id: 'date', desc: true }] : [],
          ...initialState,
        }}
        {...rest}
      />
    </Box>
  );
}
